import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Popover } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Checkbox from '@/components/@IntranetLibrary/Checkbox';
import {
  Chevron,
  ChipButton,
  ChipLabel,
  popoverPaperSx,
} from '@/components/Approval/RightContainer/FilterBar/styles';

export type MoreFilterOption = { key: string; label: string };

type Props = {
  options: MoreFilterOption[];
  /** 현재 상단에 칩으로 떠 있는 부가 필터 key 목록 */
  activeKeys: string[];
  /** 체크 토글 — 켜면 상단에 칩 추가, 끄면 칩/값 제거 */
  onToggle: (key: string) => void;
};

/**
 * "필터 더보기" — 지라 이슈검색처럼 부가 필터를 켜고 끄는 선택 목록.
 * 여기서 항목을 체크하면 상단 필터바에 해당 필터 칩이 추가되고, 그 칩에서 상세를 설정한다.
 */
const MoreFiltersPopover = ({ options, activeKeys, onToggle }: Props) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <ChipButton
        type="button"
        selected={false}
        open={open}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <ChipLabel>{t('필터 더보기')}</ChipLabel>
        <Chevron open={open} aria-hidden>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M5.833 8L10 12.167L14.167 8"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Chevron>
      </ChipButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { ...popoverPaperSx, width: '240px' } }}
      >
        <List>
          {options.map((o) => (
            <Row
              key={o.key}
              className={'v2cb-hover-target'}
              onClick={() => {
                onToggle(o.key);
                // 항목을 꺼내면 더보기는 닫고, 새 칩이 열려 포커싱되게 한다
                setAnchorEl(null);
              }}
            >
              <Checkbox
                checked={activeKeys.includes(o.key)}
                sx={{ padding: 0 }}
              />
              <Label>{t(o.label)}</Label>
            </Row>
          ))}
        </List>
      </Popover>
    </>
  );
};

export default MoreFiltersPopover;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  * {
    cursor: pointer;
  }
`;

const Label = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: var(--dk-text, black);
`;
